import { useEffect, useState } from "react";
import { getCategories } from "@/lib/categories";
import { sampleArticles } from "@/lib/sample-articles";
import { ArticleCard } from "@/components/ArticleCard";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import type { Article } from "@/types";

export function CategoriesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const categories = getCategories();

  useEffect(() => {
    async function fetchArticles() {
      try {
        const { getArticles } = await import("@/lib/firebase");
        const data = await getArticles();
        setArticles(data.length > 0 ? data : sampleArticles);
      } catch {
        setArticles(sampleArticles);
      } finally {
        setLoading(false);
      }
    }
    fetchArticles();
  }, []);

  const filtered = selectedCategory
    ? articles.filter((a) => a.category === selectedCategory)
    : articles;

  const seoTitle = selectedCategory
    ? `${selectedCategory} — Categories`
    : "Categories";

  const seoDescription = selectedCategory
    ? `Retrouvez tous les tests et avis de formations en ligne dans la categorie ${selectedCategory}.`
    : "Parcourez les tests de formations en ligne par categorie : developpement web, data science, design, marketing digital, business et plus.";

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="space-y-4 animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-7 bg-muted rounded w-20" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-5 py-10">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/categories"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Categories", path: "/categories" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Categories de formations en ligne",
          description: seoDescription,
          url: "https://le-cobaye.fr/categories",
          inLanguage: "fr",
          isPartOf: {
            "@type": "WebSite",
            name: "Le Cobaye",
            url: "https://le-cobaye.fr",
          },
        }}
      />

      <Breadcrumbs
        items={[
          { label: "Accueil", href: "/" },
          { label: "Categories" },
        ]}
      />

      <h1 className="text-xl font-bold tracking-tight mb-6">Categories</h1>

      <nav aria-label="Filtres par categorie" className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            selectedCategory === null
              ? "bg-foreground text-background border-foreground"
              : "bg-transparent text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          Tous
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              selectedCategory === cat
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:text-foreground"
            }`}
          >
            {cat}
          </button>
        ))}
      </nav>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">
          Aucun article dans cette categorie.
        </p>
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </main>
  );
}
